import React, { useMemo } from 'react';

import { Typography, FormControl, InputLabel, Select, MenuItem, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { Badge, Company, CompanyApplication, Contact } from '../../../interfaces/users.interface';
import { WORKFLOW_MAP } from '../../../services/workflow';
import { Engagement } from '../../../interfaces/engagements';
import { BADGE_IMAGE_DATA_MAP, BADGE_LABEL_IMAGEE_MAP, BLUE_BADGE_LABEL, GOLD_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, SILVER_BADGE_LABEL } from '../../../services/badge';
import { isWorkflowFiveThreeOneOrGreater } from '../../../services/questionnaires';
import { WorkflowTypeKey } from '../../../interfaces/workflow.interface';

const LabelWithImage = ({ badgeType, blueBadgeSrc }: { badgeType: string, blueBadgeSrc?: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <div
      style={{
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <img
        src={ !!blueBadgeSrc ? blueBadgeSrc : BADGE_LABEL_IMAGEE_MAP[badgeType]}
        alt="TPN Logo"
        style={{
          width: '80%',
          height: '80%',
          objectFit: 'contain',
          padding: '2px',
        }}
      />
    </div>
    <span>{badgeType === BLUE_BADGE_LABEL ? 'Questionnaire' : 'Assessment'} Report</span>
  </div>
);

const SiteAppReportType = ({
  type,
  setType,
  siteOrApplication,
  setSiteOrApplication,
  questionnaireType,
  questionnaireTypes,
  setQuestionnaireType,
  selectedReportTypes,
  setSelectedReportTypes,
  availableBadges,
  siteList,
  applicationList,
}: {
  type: string;
  inheritedReport?: Engagement | null;
  setType: (type: string) => void;
  siteOrApplication: Contact | CompanyApplication | null;
  setSiteOrApplication: (siteOrApp: Contact | CompanyApplication | null) => Promise<void>;
  questionnaireType: WorkflowTypeKey | null;
  questionnaireTypes: string[];
  availableBadges?: Badge[];
  selectedReportTypes: string[];
  setSelectedReportTypes: (reportTypes: string[]) => void;
  setQuestionnaireType: (questionnaireType: WorkflowTypeKey | null) => void;
  siteList: Contact[];
  applicationList: CompanyApplication[];
}) => {
  const showBlueBadge = useMemo(() => availableBadges?.some((badge) => badge.title === BLUE_BADGE_LABEL), [availableBadges]);

  const highestAssessmentBadgeLabel = useMemo(() => {
    if (availableBadges?.some((badge) => badge.title === GOLD_STAR_BADGE_LABEL)) return GOLD_STAR_BADGE_LABEL;
    if (availableBadges?.some((badge) => badge.title === GOLD_NEXT_GEN_BADGE_LABEL)) return GOLD_NEXT_GEN_BADGE_LABEL;
    if (availableBadges?.some((badge) => badge.title === SILVER_BADGE_LABEL)) return SILVER_BADGE_LABEL;
    if (availableBadges?.some((badge) => badge.title === GOLD_BADGE_LABEL)) return GOLD_BADGE_LABEL;
    return null;
  }, [availableBadges]);

  const blueBadgeSrc = questionnaireType && isWorkflowFiveThreeOneOrGreater(questionnaireType) ? BADGE_IMAGE_DATA_MAP.blueNextGen.src : BADGE_IMAGE_DATA_MAP.blue.src;

  return (
        <>
        <Typography variant="h6">Which Site/Application and Report Are You Sharing?</Typography>
        <FormControl fullWidth>
          <InputLabel id="type-label">Type</InputLabel>
          <Select
            labelId="type-label"
            label="Type"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setSiteOrApplication(null);
              setQuestionnaireType(null);
              setSelectedReportTypes([]);
            }}
          >
            <MenuItem value="site">Site</MenuItem>
            <MenuItem value="application">Application</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="site-application-label">Select {type === 'site' ? 'Site' : 'Application'}</InputLabel>
          <Select
            label={`Select ${type === 'site' ? 'Site' : 'Application'}`}
            labelId="site-application-label"
            value={siteOrApplication ? siteOrApplication.id : ''}
            onChange={(e) => {
              const selectedItem =
                type === 'site'
                  ? siteList.find((site) => site.id === e.target.value)
                  : applicationList.find((app) => app.id === e.target.value);
              
              setSiteOrApplication(selectedItem || null);
              setQuestionnaireType(null);
            }}
            disabled={!type}
          >
            {(type === 'site' ? siteList : applicationList).map((item) => (
              <MenuItem key={item.id} value={item.id}>{type === 'site' ? (item as Company).name : (((item as CompanyApplication).application).name)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Questionnaire Type Selection */}
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="questionnaire-type-label">Questionnaire Version</InputLabel>
          <Select
            labelId="questionnaire-type-label"
            label="Questionnaire Version"
            value={questionnaireType}
            onChange={(e) => setQuestionnaireType(e.target.value as WorkflowTypeKey)}
          >
            {questionnaireTypes.map((qType) => (
              <MenuItem key={qType} value={qType}>{WORKFLOW_MAP[qType as keyof typeof WORKFLOW_MAP].label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Shield Type Selection */}
        <FormGroup sx={{ pl: 2, display: 'flex', flexDirection: 'row', gap: 2 }}>
        { showBlueBadge && (
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedReportTypes.includes(BLUE_BADGE_LABEL)}
                onChange={(e) => {
                  const newReportTypes = e.target.checked
                    ? [...selectedReportTypes, BLUE_BADGE_LABEL]
                    : selectedReportTypes.filter((b) => b !== BLUE_BADGE_LABEL);
                  setSelectedReportTypes(newReportTypes);
                }}
              />
            }
            label={<LabelWithImage badgeType={BLUE_BADGE_LABEL} blueBadgeSrc={blueBadgeSrc}/>}
          />
        )}
        { highestAssessmentBadgeLabel && (
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedReportTypes.includes(highestAssessmentBadgeLabel)}
                onChange={(e) => {
                  const newReportTypes = e.target.checked
                    ? [...selectedReportTypes, highestAssessmentBadgeLabel]
                    : selectedReportTypes.filter((b) => b !== highestAssessmentBadgeLabel);
                  setSelectedReportTypes(newReportTypes);
                }}
              />
            }
            label={<LabelWithImage badgeType={highestAssessmentBadgeLabel} />}
          />
        )}
        { !showBlueBadge && !highestAssessmentBadgeLabel && questionnaireType && (
          <Typography variant="body2" sx={{ color: 'gray' }}>No reports available for the selected {type} and Questionnaire version</Typography>
        )}
        </FormGroup>
      </>
  );
};

export default SiteAppReportType;